import { BoxProps } from './types';
// The green cards below are green-zone DATA, so they are typed with the green box shape (it owns the
// green-only `entryLabel` slot). Type-only import — nothing from the Punchup tree is pulled in at runtime.
import type { BoxProps as GreenBoxProps } from 'features/common/components/Punchup/composites/tiles/PunchupHighlightsCollection/types';
import i18n from 'locals/i18n';

export const TOTAL_EVENTS = 'total_events';
export const TOTAL_ATTENDEES = 'total_attendees';
export const TICKETS_SOLD = 'tickets_sold';
export const LOTTERY = 'lottery';
export const TOTAL_PAYOUT = 'total_payout';
export const TICKETS_COMPED = 'tickets_comped';
export const CHECK_INS = 'num_of_scans';
export const SCAN_INS = 'num_of_scans';
export const EVENT_SCANS = 'event_scans';
export const SHOW_RATE = 'show_rate';
export const PAGE_VIEWS = 'page_views';

const ATTENDEES_CARD: BoxProps = {
	property: 'total_attendees',
	dataCy: 'attendees-card',
	icon: 'user2White',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-totalAttendees'),
	buttonLabel: i18n.t('highlights-view-attendees'),
	onClick: undefined,
};

const CHECK_INS_CARD: BoxProps = {
	property: CHECK_INS,
	dataCy: 'check-ins-card',
	icon: 'userCheckWhite',
	stat: '',
	cardLabel: i18n.t('highlights-checkins'),
};

const COMPED_CARD: BoxProps = {
	property: TICKETS_COMPED,
	dataCy: 'comped-tickets-card',
	icon: 'mailWhite',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-comped'),
	buttonLabel: i18n.t('highlights-send-tickets'),
};

const EVENTS_CARD: BoxProps = {
	property: TOTAL_EVENTS,
	dataCy: 'events-card',
	icon: 'calendarWhite',
	stat: '',
	cardLabel: i18n.t('highlights-totalEvents'),
	buttonLabel: '',
};

// const INVITATIONS_CARD: BoxProps = { property: '',dataCy:'', icon: '', stat: '', cardLabel: i18n.t(''), buttonLabel: '' };

// const PAGE_VIEWS_CARD: BoxProps = {
// 	property: PAGE_VIEWS,
// 	dataCy: '',
// 	icon: 'planner',
// 	stat: '',
// 	cardLabel: i18n.t('highlights-page-views'),
// 	buttonLabel: i18n.t('highlights-view-analytics'),
// };

const PAYOUT_CARD: BoxProps = {
	property: TOTAL_PAYOUT,
	dataCy: 'payout-card',
	icon: 'dollarSignWhite',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-yourPayout'),
	buttonLabel: i18n.t('highlights-view-transactions'),
};

const SCAN_INS_CARD: BoxProps = {
	property: SCAN_INS,
	dataCy: 'scan-ins-card',
	icon: 'userCheckWhite',
	stat: '',
	cardLabel: i18n.t('highlights-scanins'),
};

const SHOW_RATE_CARD: BoxProps = {
	property: SHOW_RATE,
	dataCy: 'show-rate-card',
	icon: '',
	stat: '',
	cardLabel: i18n.t('highlights-show-rate'),
};

const TICKETS_SOLD_CARD: BoxProps = {
	property: TICKETS_SOLD,
	dataCy: 'tickets-sold-card',
	icon: 'ticketVertical',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-ticketsSold'),
	buttonLabel: i18n.t('highlights-view-analytics'),
};

const LOTTERY_CARD: BoxProps = {
	property: LOTTERY,
	dataCy: 'lottery-card',
	icon: 'dices',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-lottery'),
	buttonLabel: i18n.t('highlights-view-lottery'),
};

const EVENT_SCANS_CARD: BoxProps = {
	property: EVENT_SCANS,
	dataCy: 'event-scans-card',
	icon: 'userCheckWhite',
	stat: '',
	cardLabel: i18n.t('dashboard-highlights-eventScans'),
};

// GREEN-zone card variants. The base cards above keep the develop copy for the native/Home/Analytics
// (red) arrays (the strict gate); these `-green` siblings carry the redesigned green-dashboard copy and
// are used ONLY by NORMAL_DASHBOARD_HIGHLIGHTS. See docs/green-zone-parity-followup.md → D1a.
//
// `entryLabel` is the bottom-anchored orange entry line the green tile renders at its foot (PUN-4942):
// the visible "go here" affordance for a click that already spans the whole tile. The arrow belongs to
// the tile, never to these strings.
const ATTENDEES_CARD_GREEN: GreenBoxProps = {
	...ATTENDEES_CARD,
	cardLabel: i18n.t('dashboard-highlights-totalAttendees-green'),
	buttonLabel: i18n.t('highlights-view-attendees-green'),
	entryLabel: i18n.t('highlights-entry-attendees-green'),
};
const COMPED_CARD_GREEN: GreenBoxProps = {
	...COMPED_CARD,
	cardLabel: i18n.t('dashboard-highlights-comped-green'),
	buttonLabel: i18n.t('highlights-send-tickets-green'),
	entryLabel: i18n.t('highlights-entry-comped-green'),
};
const PAYOUT_CARD_GREEN: GreenBoxProps = {
	...PAYOUT_CARD,
	cardLabel: i18n.t('dashboard-highlights-yourPayout-green'),
	buttonLabel: i18n.t('highlights-view-transactions-green'),
	entryLabel: i18n.t('highlights-entry-revenue-green'),
};
// Lottery is the one tile PUN-4942 gave no entry copy of its own, so its existing green line serves as
// the entry text. It moves off `buttonLabel` rather than being copied: the description row and the
// entry row would otherwise print the same sentence twice on the same tile.
const LOTTERY_CARD_GREEN: GreenBoxProps = {
	...LOTTERY_CARD,
	cardLabel: i18n.t('dashboard-highlights-lottery-green'),
	buttonLabel: undefined,
	entryLabel: i18n.t('highlights-view-lottery-green'),
};
const EVENT_SCANS_CARD_GREEN: GreenBoxProps = {
	...EVENT_SCANS_CARD,
	cardLabel: i18n.t('dashboard-highlights-eventScans-green'),
	buttonLabel: i18n.t('highlights-view-scans-green'),
	entryLabel: i18n.t('highlights-entry-scans-green'),
};

export const REG_DASHBOARD_HIGHLIGHTS: BoxProps[] = [ATTENDEES_CARD]; // will include [INVITATIONS, PAGE_VIEWS,ATTENDEES, CHECK_INS] in the future

// GREEN zone (Punchup iframe dashboard, EventHighlights): stat tiles only — every entry navigates to a
// sub-page. The Analytics-nav tile was removed (PUN-4942 req. 4): the green Analytics sub-page is blocked
// on separate reporting work, so it is no longer surfaced from the dashboard. Do NOT reuse this array for
// the native dashboard — its cards are the *_CARD_GREEN copies, which carry green-only copy; the native
// dashboard uses NATIVE_DASHBOARD_HIGHLIGHTS below.
export const NORMAL_DASHBOARD_HIGHLIGHTS: GreenBoxProps[] = [
	LOTTERY_CARD_GREEN,
	ATTENDEES_CARD_GREEN,
	COMPED_CARD_GREEN,
	EVENT_SCANS_CARD_GREEN,
	PAYOUT_CARD_GREEN,
];

// RED zone (native dark-theme dashboard, tixologist/Highlights): the original tile set — every
// card here has a handler in the native Highlights useEffect (TICKETS_SOLD carries the
// "View Analytics" button). Kept separate from NORMAL_DASHBOARD_HIGHLIGHTS so the green-zone copy and
// tile set never leak back into the native row.
export const NATIVE_DASHBOARD_HIGHLIGHTS: BoxProps[] = [
	PAYOUT_CARD,
	LOTTERY_CARD,
	TICKETS_SOLD_CARD,
	ATTENDEES_CARD,
	COMPED_CARD,
	EVENT_SCANS_CARD,
];
export const HOME_HIGHLIGHTS: BoxProps[] = [EVENTS_CARD, ATTENDEES_CARD];
export const REG_EVENT_ANALYTICS = [ATTENDEES_CARD, CHECK_INS_CARD, SHOW_RATE_CARD];
export const NORMAL_EVENT_ANALYTICS = [ATTENDEES_CARD, SCAN_INS_CARD, SHOW_RATE_CARD];
export const ALL_EVENT_ANALYTICS = [ATTENDEES_CARD, TICKETS_SOLD_CARD, SHOW_RATE_CARD];
